import React, { memo, useCallback, useEffect, useMemo } from 'react';

import { Box, Button } from '@mui/material';
import AdminSlideDrawer from '@sharedCR/common/AdminSlideDrawer';
import FormGenerator from '@sharedCR/common/formGenerator';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import { COMMON_REQUEST_DATETIME_FORMAT } from 'constants/date';
import useForm from 'hooks/useForm';
import { getConfigInfo } from 'utils/loanManagement';

import { formContentStyles } from './InvestorConditionDrawer.styles';
import {
  CONDITION_DATES_FORM_CONFIG,
  CONDITION_TEXT_FORM_CONFIG,
  INVESTOR_CONDITION_FORM_CONFIG,
} from './InvestorConditionModal.config';
import { InvestorConditionsTestIds } from './InvestorConditions.testIds';

const formatDate = date => {
  if (!date) {
    return null;
  }

  return dayjs.isDayjs(date) ? date.format(COMMON_REQUEST_DATETIME_FORMAT) : date;
};

const InvestorConditionDrawer = ({ open, condition, onSave, onClose }) => {
  const applicationData = useMemo(
    () => ({
      conditionText: condition?.conditionText ?? '',
      dateAdded: condition?.dateAdded ?? dayjs().format(COMMON_REQUEST_DATETIME_FORMAT),
      dateResponded: condition?.dateResponded ?? null,
      dateCleared: condition?.dateCleared ?? null,
    }),
    [condition],
  );

  const { initialFormValues, fieldsConfig, observableFieldsNames, schema } = useMemo(
    () => getConfigInfo({ applicationData, config: INVESTOR_CONDITION_FORM_CONFIG }),
    [applicationData],
  );

  const {
    form,
    handleChange,
    validationErrors,
    setInForm,
    validateField,
    validateForm,
    setSchema,
    setForm,
    setInitialFormValues,
  } = useForm(initialFormValues, fieldsConfig, observableFieldsNames);

  useEffect(() => {
    setForm(initialFormValues);
    setInitialFormValues(initialFormValues);
  }, [setForm, setInitialFormValues, initialFormValues]);

  useEffect(() => {
    setSchema(schema);
  }, [schema, setSchema]);

  const handleSave = useCallback(async () => {
    const isValid = await validateForm({ noFormChangedValidate: true });

    if (!isValid) {
      return;
    }

    const recordData = {
      conditionText: form.conditionText ?? '',
      dateAdded: formatDate(form.dateAdded),
      dateResponded: formatDate(form.dateResponded),
      dateCleared: formatDate(form.dateCleared),
    };

    onSave(recordData, condition?.id);
    onClose();
  }, [form, onSave, condition?.id, onClose, validateForm]);

  const drawerActions = useMemo(
    () => (
      <Button data-testid={InvestorConditionsTestIds.DRAWER_SAVE_BUTTON} onClick={handleSave}>
        Save
      </Button>
    ),
    [handleSave],
  );

  const formGeneratorProps = useMemo(
    () => ({
      form,
      handleChange,
      setInForm,
      validateField,
      validationErrors,
    }),
    [form, handleChange, setInForm, validateField, validationErrors],
  );

  const drawerContent = useMemo(
    () => (
      <Box sx={formContentStyles}>
        <FormGenerator
          config={CONDITION_TEXT_FORM_CONFIG}
          numberOfColumns={1}
          {...formGeneratorProps}
        />
        <FormGenerator
          config={CONDITION_DATES_FORM_CONFIG}
          numberOfColumns={3}
          {...formGeneratorProps}
        />
      </Box>
    ),
    [formGeneratorProps],
  );

  if (!open) {
    return null;
  }

  return (
    <AdminSlideDrawer
      actions={drawerActions}
      content={drawerContent}
      open={open}
      title={condition ? 'Edit Condition' : 'Add Condition'}
      onClose={onClose}
    />
  );
};

InvestorConditionDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  condition: PropTypes.shape({
    id: PropTypes.string,
    conditionText: PropTypes.string,
    dateAdded: PropTypes.string,
    dateResponded: PropTypes.string,
    dateCleared: PropTypes.string,
  }),
};

export default memo(InvestorConditionDrawer);
